import Info from "./comps/info";
import { generateDownloadOriginalArtworkURL, getColor } from "../../utils/general";
import FileUploadComponent from "./comps/fileUpload";
import formattedDate from "../../utils/formatted-date";
import { getBackendAPIDomain, getBackendApiUrl } from "../../config";
import ArtInformationAndQRCode from "./comps/artInformationAndQRCode";
import ImageSelector from "./comps/imageSelector";
import PrintTemplate from "./print-template";
import UserWorkFlow from "./comps/userWorkflow";
import { useSnapshot } from "valtio";
import appModel from "../../models/app-model";
import { useEffect, useState } from "react";
import { ArtworkType, ArtworkItemType, WizardDocConversation, DesignDoc, WizardDocNote, SewDiskCharge, DefaultCustomerData } from "../../types/api-types";

import 'react-tabs/style/react-tabs.css';
import { IProdOrderLineItem } from "../../types/iprod-order";
import { InternalArtStates, toArtState, ArtStatesDescriptions, ArtworkFolder } from "../../types/art-status";
import { AppPages } from "../../app-pages";
import { archiveDesign, deleteDesign, getPriceBreaks, updateDesignDetails } from "../../models/app-actions";
import { ArtDetailsLoading } from "./loading";
import { downloadProductionFile, getFileExtension } from "../../utils/download-file";
import { DEFAULT_IMAGE } from "../../config";
import CustomModal from "../../components/customMessageModal/customMsgModal";
import { getBusinessCentralURLParams } from "../../utils/general";
import ArtDetailsModal from "./comps/artDetailsModal";
import ImagePreview from "./comps/imagePreivew";
import formatDateString from "../../utils/formatted-date";
import { splitCamelCase } from "../../components/cardDetails/cardValues";
import { addAuthorizationHeader, getAdminToken, getCustomerToken } from "../../api/network";
import { getEasyViewEditConfigBaseURL$, importToEasyView, isEdge } from "../../api/backend";
import { impersonate } from "../../api/backend";

// Styles
import styles from './art-details.module.css'; // index.tsx
import artStyles from '../art-dashboard/art.module.css';
import { getBCArtFeeForDocumentId, getOriginalSewDiskChargesForDocumentId, getSewDiskChargesForDocumentId, getWizardDocById } from "../../api/backend";
import { isAWAWProductType } from "../../utils/artDetailsUtils";

function ViewMoreIcon() {
  return (
    <svg fill="none" viewBox="0 0 24 24"
      height="24px"
      width="24px" strokeWidth={1.5} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M8.625 12a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm0 0H8.25m4.125 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm0 0H12m4.125 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm0 0h-.375M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
    </svg>
  )
}

const flex = "flex-wrap gap-6 px-4 sm:px-6 lg:px-8";

type MainBarProps = {
  adminId: string | null;
  page: AppPages;
  orderNo: string | null;
  lineNo: string | null;
  archived?: boolean;
  itemId: string;
  isIncomplete: boolean;
};

const MainBar: React.FC<MainBarProps> = ({
  adminId,
  page,
  orderNo,
  lineNo,
  archived = false,
  itemId,
  isIncomplete,
}) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [openArchiveModal, setOpenArchiveModal] = useState<boolean>(false);
  const [openDeleteModal, setOpenDeleteModal] = useState<boolean>(false);

  const handleBack = () => {
    sessionStorage.setItem("isGoBack", "true");
    window.history.back();
  };

  const handlePrint = () => {
    window.print();
  };

  const handleArchiveClick = () => {
    setOpenArchiveModal(true);
  }

  const handleDeleteClick = () => {
    setOpenDeleteModal(true);
  };

  const updateArchivedState = async (itemId: string) => {
    setIsLoading(true);
    try {
      const updateItemState = await archiveDesign(itemId);
      console.log(updateItemState);

    } catch (err: any) {
      console.log(err);
    } finally {
      setIsLoading(false);
      await updateDesignDetails();
    }
  }

  const deleteDesignItem = async (itemId: string) => {
    setIsLoading(true);
    try {
      await deleteDesign(itemId);
      console.log(`Design with ID ${itemId} deleted.`);
      handleBack();
    } catch (err: any) {
      console.log(err);
    } finally {
      setIsLoading(false);
    }
  };

  const businessCentralParams = getBusinessCentralURLParams();
  const currentUrl = new URL(window.location.href)
  const isDirectLink = (currentUrl.searchParams.get("directLink") ?? "").toLowerCase() == "true"
  const isProduction = businessCentralParams["SourceType"] == "Production";

  return (
    <div className={[flex, styles.mainBar].join(' ')}>
      {page === AppPages.DetailsArtist &&
        <h3 className="text-base font-light leading-6 text-white uppercase">
          ART & CUSTOMER SERVICE MODE
        </h3>}
      {!adminId && !isDirectLink &&
        <button
          onClick={handleBack}
          className={`${artStyles.orangeBtn} flex flex-row gap-1 text-white hover:bg-blue-500`}
        >
          Go Back
        </button>
      }
      {page === AppPages.DetailsProduction &&
        <h3 className={["text-base font-light leading-6 text-white uppercase", styles.qrCodeContainer].join(' ')}>
          <ArtInformationAndQRCode
            LineNo={lineNo}
            DocNo={orderNo}
            isProductionMode={page == AppPages.DetailsProduction}
          />
        </h3>
      }
      <div className={styles.navBarButtonsWrapper}>
        {/* Archive/Unarchive Confirmation Modal */}
        <CustomModal
          isOpen={openArchiveModal}
          onClose={() => setOpenArchiveModal(false)}
          message={`Are you sure you want to ${archived ? "unarchive" : "archive"}?`}
          onConfirm={() => updateArchivedState(itemId)}
          processing={isLoading}
          confirmButtonText="Confirm"
          showCancelButton={false}
          isCustomerView={!adminId}
        />
        <CustomModal
          isOpen={openDeleteModal}
          onClose={() => setOpenDeleteModal(false)}
          message="Are you sure you want to permanently delete this design? This action cannot be undone."
          onConfirm={() => deleteDesignItem(itemId)}
          processing={isLoading}
          confirmButtonText="Delete"
          showCancelButton={false}
          isCustomerView={!adminId}
        />
        {!isProduction && (
          <button
            type="button"
            className={`${styles.archiveBtn} non-printable`}
            onClick={handleArchiveClick}
          >
            <div className={styles.archive}>
              {!archived ? (
                // archive
                <>
                  <svg className="h-8 w-8" viewBox="0 0 24 24" fill="none">
                    <path d="M2 12.0002C2 7.28611 2 4.92909 3.46447 3.46462C4.70529 2.2238 6.58687 2.03431 10 2.00537M22 12.0002C22 7.28611 22 4.92909 20.5355 3.46462C19.2947 2.2238 17.4131 2.03431 14 2.00537" stroke="#fff" strokeWidth={2} strokeLinecap="round" />
                    <path d="M10 22C7.19974 22 5.79961 22 4.73005 21.455C3.78924 20.9757 3.02433 20.2108 2.54497 19.27C2 18.2004 2 16.8003 2 14C2 11.1997 2 9.79961 2.54497 8.73005C3.02433 7.78924 3.78924 7.02433 4.73005 6.54497C5.79961 6 7.19974 6 10 6H14C16.8003 6 18.2004 6 19.27 6.54497C20.2108 7.02433 20.9757 7.78924 21.455 8.73005C22 9.79961 22 11.1997 22 14C22 16.8003 22 18.2004 21.455 19.27C20.9757 20.2108 20.2108 20.9757 19.27 21.455C18.2004 22 16.8003 22 14 22" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" />
                    <path d="M12 11L12 17M12 17L14.5 14.5M12 17L9.5 14.5" stroke="#fff" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  <span>ARCHIVE</span>
                </>
              ) : (
                // un-archive
                <>
                  <svg className="h-8 w-8" viewBox="0 0 24 24" fill="none">
                    <path d="M2 11.9997C2 7.28562 2 4.9286 3.46447 3.46413C4.70529 2.22331 6.58687 2.03382 10 2.00488M22 11.9997C22 7.28562 22 4.9286 20.5355 3.46413C19.2947 2.22331 17.4131 2.03382 14 2.00488" stroke="#fff" strokeWidth={2} strokeLinecap="round" />
                    <path d="M10 22C7.19974 22 5.79961 22 4.73005 21.455C3.78924 20.9757 3.02433 20.2108 2.54497 19.27C2 18.2004 2 16.8003 2 14C2 11.1997 2 9.79961 2.54497 8.73005C3.02433 7.78924 3.78924 7.02433 4.73005 6.54497C5.79961 6 7.19974 6 10 6H14C16.8003 6 18.2004 6 19.27 6.54497C20.2108 7.02433 20.9757 7.78924 21.455 8.73005C22 9.79961 22 11.1997 22 14C22 16.8003 22 18.2004 21.455 19.27C20.9757 20.2108 20.2108 20.9757 19.27 21.455C18.2004 22 16.8003 22 14 22" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" />
                    <path d="M12 17L12 11M12 11L14.5 13.5M12 11L9.5 13.5" stroke="#fff" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  <span>UNARCHIVE</span>
                </>
              )}
            </div>
          </button>
        )}
        {(adminId || isIncomplete) && (
          <button
            type="button"
            className={`${styles.archive} non-printable`}
            onClick={handleDeleteClick}
          >
            <div className={[styles.archive, styles.dltBtn].join(' ')}>
              <svg className="h-8 w-8" viewBox="0 0 24 24" fill="none">
                <path d="M3 6L5 6L21 6" stroke="#fff" strokeWidth={2} strokeLinecap="round" />
                <path d="M6 6L6 19C6 20.1046 6.89543 21 8 21H16C17.1046 21 18 20.1046 18 19V6" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" />
                <path d="M10 11L10 17M14 11L14 17" stroke="#fff" strokeWidth={2} strokeLinecap="round" />
                <path d="M9 3H15M15 3C16.1046 3 17 3.89543 17 5H7C7 3.89543 7.89543 3 9 3H15Z" stroke="#fff" strokeWidth={2} strokeLinecap="round" />
              </svg>
              <span>DELETE</span>
            </div>
          </button>
        )}
        {/* {ucMaxButtons} */}
        {adminId &&
          <>
            <button
              className={`${artStyles.orangeBtn} ${styles.printBtn} non-printable`}
              onClick={handlePrint}>
              <svg
                width={"22px"}
                height={"22px"}
                xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round"
                  d="M6.72 13.829c-.24.03-.48.062-.72.096m.72-.096a42.415 42.415 0 0 1 10.56 0m-10.56 0L6.34 18m10.94-4.171c.24.03.48.062.72.096m-.72-.096L17.66 18m0 0 .229 2.523a1.125 1.125 0 0 1-1.12 1.227H7.231c-.662 0-1.18-.568-1.12-1.227L6.34 18m11.318 0h1.091A2.25 2.25 0 0 0 21 15.75V9.456c0-1.081-.768-2.015-1.837-2.175a48.055 48.055 0 0 0-1.913-.247M6.34 18H5.25A2.25 2.25 0 0 1 3 15.75V9.456c0-1.081.768-2.015 1.837-2.175a48.041 48.041 0 0 1 1.913-.247m10.5 0a48.536 48.536 0 0 0-10.5 0m10.5 0V3.375c0-.621-.504-1.125-1.125-1.125h-8.25c-.621 0-1.125.504-1.125 1.125v3.659M18 10.5h.008v.008H18V10.5Zm-3 0h.008v.008H15V10.5Z" />
              </svg>
            </button>
          </>
        }
        {!adminId && page === AppPages.DetailsArtist && (
          <button
            type="button"
            className={`${styles.deleteBtn} non-printable`}
            onClick={handleDeleteClick}
          >
            <div className={styles.delete}>
              <svg className="h-8 w-8" viewBox="0 0 24 24" fill="none">
                <path d="M6 6L18 18M18 6L6 18" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              <span>DELETE</span>
            </div>
          </button>
        )}
      </div>
    </div>
  );
};

function PageSwitch(props: { If: AppPages[], children: React.ReactNode }) {
  const appSnap = useSnapshot(appModel);

  if (props.If.includes(appSnap.page)) {
    return props.children
  } else {
    return <></>
  }
}

function DesignDetailPage() {
  const appSnap = useSnapshot(appModel);
  const docId = appModel.artId;
  const prodLineItems = appSnap.prodLineItems
  const currentPage = appSnap.page;
  const lineNo = appSnap.lineNo;
  const orderNo = appSnap.orderNo;
  const wizardDoc = appSnap.activeDesignDoc;
  const archived = appSnap.activeDesignDoc?.archived;
  let wizardDocData = wizardDoc?.wizardData;
  const wizardDocFullData = wizardDoc?.wizardData;
  const productType = wizardDoc?.docType === "LNTransfer" && wizardDoc?.wizardID === 'PreSpacedLetters' && typeof wizardDocData.szItemCode == 'string' && wizardDocData.szItemCode.substring(0, 2) == 'ZW' ? 'ZippWeedText' : wizardDocData.productType ?? wizardDoc?.wizardID;
  const wizardDocConversations = appSnap.activeConversation as WizardDocConversation[] || [];
  const adminId = appSnap.adminId;
  const customerId = appSnap.customerId;

  const [disabledButtonClass,] = useState<string>(`${styles.disabledBtn} bg-gray-300 text-gray-500 cursor-not-allowed`);
  const [productionArtwork, setProductionArtwork] = useState<ArtworkType | null>(null);
  const [productionArtworkPreview, setProductionArtworkPreview] = useState<ArtworkType | null>(null);
  const [originalArtwork, setOriginalArtwork] = useState<ArtworkType | null>(null);
  const isEasyView = wizardDoc?.wizardID === "EasyView";
  const isDTFTransfer = wizardDoc?.wizardID === "DTFTransfer";
  const isApproved = appSnap.activeDesignDoc?.statusID == InternalArtStates.APPROVED;
  const isIncomplete = appSnap.activeDesignDoc?.statusID == InternalArtStates.INCOMPLETE;
  const enableEasyViewLink = ( isEasyView || isDTFTransfer ) && ( adminId || customerId == appSnap.activeDesignDoc?.customerID ) && isApproved ;
  const isLNTransfer = wizardDoc?.docType === "LNTransfer";
  const isWizardDesign = wizardDoc?.docType === "WizardDesign";
  const isBarcelona = wizardDoc?.docType === "NameAndNumberKit" && productType === "Barcelona";
  const isNhl = wizardDoc?.docType === "NameAndNumberKit" && productType === "NHL";
  const isEmblemWizard = wizardDoc?.docType === 'EmblemWizard';
  const fullWizardData = wizardDocData
  wizardDocData = Array.isArray(wizardDocData) ? wizardDocData[0] : wizardDocData;
  wizardDocData = isEasyView ? { ...wizardDocData, ...wizardDocData?.extendedData } : wizardDocData;
  const [activeImage, setActiveImage] = useState('originalArtwork' as ArtworkFolder);
  const docObject = wizardDoc;
  const statusColor = wizardDoc ? getColor(toArtState(wizardDoc.statusID)) : ''
  const activeStatus = toArtState(appSnap.activeDesignDoc?.statusID ?? InternalArtStates.OPEN)
  const activeStatusDescription = ArtStatesDescriptions[activeStatus].description;
  const [imageSrc, setImgSrc] = useState<string>('');
  const [sewFileImg, setSewFileImg] = useState<ArtworkItemType | null>(null);
  const shippingAgentName = appSnap.prodLineItems.filter(x => !!x.shippingAgentServiceCode)[0]?.shippingAgentName ?? "--";
  const shippingAgentServiceDescription = appSnap.prodLineItems.filter(x => !!x.shippingAgentServiceCode)[0]?.shippingAgentServiceDescription ?? "--";
  const originalDocID = wizardDoc?.originalDocID;
  const [priceBreaks, setPriceBreaks] = useState<{ FromQty: number, ToQty: number, ItemPrice: number }[]>([]);
  const [priceBreaksMultiple, setPriceBreaksMultiple] = useState<number>(0);
  const [openMoreDetailsModal, setOpenMoreDetailsModal] = useState<boolean>(false);
  const [openImagePreview, setOpenImagePreview] = useState<boolean>(false);
  const [currentSewDiskCharge, setCurrentSewDiskCharge] = useState<SewDiskCharge|undefined>(undefined)
  const [artFeeData, setArtFeeData] = useState<undefined|Awaited<ReturnType<typeof getBCArtFeeForDocumentId>>>(undefined)
  const [defaultCustomerData, setDefaultCustomerData] = useState<DefaultCustomerData>({
    email: '',
    fullName: '',
    phone: ''
  });
  const [lnTailPrice, setLNTailPrice] = useState<number>(0)
  const [lnTailCutInPrice, setLNTailCutInPrice] = useState<number>(0)
  const [lnSewDiskPrice, setLNSewDiskPrice] = useState<number>(0)
  const [lnNameDropPrice, setLNNameDropPrice] = useState<number>(0)
  const [lnVelcroPrice, setLNVelcroPrice] = useState<number>(0)
  const [openingEV, setOpeningEV] = useState(false)

 useEffect(() => {
    const setLNArtFees = function(doc:DesignDoc) {
      const dataLN = doc.BoM as {
        items: {
          productId: string;
          uomId: string;
          quantity: number;
          isTail?: boolean;
          isTailCutIn?: boolean;
          isSewDisk?: boolean;
          isNameDrop?: boolean;
          isVelcro?: boolean;
          price?:number;
        }[];
      };

      let tailPrice = 0
      let tailCutInPrice = 0
      let sewDiskPrice = 0
      let nameDropPrice = 0
      let velcroPrice = 0

      if(dataLN && dataLN.items && dataLN.items.length) {
        dataLN.items.forEach(x => {
          if(x.isTail && x.price) tailPrice += (x.price * x.quantity)
          if(x.isTailCutIn && x.price) tailCutInPrice += (x.price * x.quantity)
          if(x.isSewDisk && x.price) sewDiskPrice += (x.price * x.quantity)
          if(x.isNameDrop && x.price) nameDropPrice += (x.price * x.quantity)
          if(x.isVelcro && x.price) velcroPrice += (x.price * x.quantity)
        })
      }

      setLNTailPrice(tailPrice)
      setLNTailCutInPrice(tailCutInPrice)
      setLNSewDiskPrice(sewDiskPrice)
      setLNNameDropPrice(nameDropPrice)
      setLNVelcroPrice(velcroPrice)
    }
    if (isLNTransfer && (customerId || adminId) && appSnap.activeDesignDoc) {
      if(appSnap.activeDesignDoc.isLNProof) {
        //when it's a proof and it's not been purchased we need to update the extra fees by fetching the price
        const docId = appSnap.activeDesignDoc.id
        fetch(`${getBackendApiUrl()}/design-purchases/${docId}`, {
          method: 'GET',
          headers: addAuthorizationHeader({
            'Content-Type': 'application/json'
          })
        }).then(async (purchasesResp) => {
          if (!purchasesResp.ok) throw new Error("Error getting purchase data " + await purchasesResp.text())
          const purchasesRespJson: { purchased: boolean } = await purchasesResp.json()
          console.log(`Design id ${docId} was${purchasesRespJson.purchased ? '' : ' not'} marked as purchased`)
          if(!purchasesRespJson.purchased) {
            const baseUrl = getBackendApiUrl();
            const priceResp = await fetch(`${baseUrl}/DesignPrices`,
              {
                method: 'POST',
                'headers': addAuthorizationHeader({
                  'Content-Type': 'application/json'
                }),
                body: JSON.stringify({
                  id: docId
                })
              }
            )

            if(!priceResp.ok) throw new Error("Failed to get prices");
            const wizardDoc = await getWizardDocById(docId);
            setLNArtFees(wizardDoc)
          }
          else setLNArtFees(appSnap.activeDesignDoc as DesignDoc)
        }).catch(err => {
          throw err
        });
      }
      else setLNArtFees(appSnap.activeDesignDoc as DesignDoc)
    }
  }, [customerId,adminId])

  const docData = {
    source: wizardDoc?.docType ? splitCamelCase(wizardDoc?.docType) : "--",
    submittedDate: wizardDoc?.createdDate ? formatDateString({ dateString: wizardDoc?.createdDate }) : "--",
    addedToCartDate: wizardDoc?.addedToCartDate ? formatDateString({ dateString: wizardDoc?.addedToCartDate }) : "--"
  };

  const handleImageSelect = (imageSrc: string) => {
    setImgSrc(imageSrc);
  };

  const openInEasyViewClick = async () => {
    if (openingEV) return;
    setOpeningEV(true);
    
    try {
      if (!wizardDoc?.id) {
        console.error('Missing document id for EasyView edit');
        return;
      }

      let user = "";
      let token = "";
      
      if (adminId) {
        user = wizardDoc.customerID;
        token = getAdminToken();
        if (!token) {
          console.warn('[EasyView Auth] Admin token missing, trying impersonation fallback.');
          const impersonatedUser = await impersonate(wizardDoc.customerID);
          token = impersonatedUser?.token ?? "";
          if (token) {
            console.warn('[EasyView Auth] Using impersonation token fallback.');
          }
        } else {
          console.warn('[EasyView Auth] Using admin token.');
        }
      } else {
        user = customerId!;
        token = getCustomerToken();
        console.warn('[EasyView Auth] Using customer token.');
      }

      if (!user || !token) {
        console.error('Customer ID or token is missing for EasyView edit');
        return;
      }

      if (!productionArtwork || !productionArtwork.length) {
        return;
      }

      const artwork = productionArtwork[0];
      const edge = isEdge();
      
      let productionArtworkId = (artwork.userFileId || artwork.documentId);
      if (!productionArtworkId) {
        return;
      }

      if (artwork.userFileId && artwork.userFileConfig) {
        const imported = await importToEasyView(artwork.userFileId, artwork.userFileConfig, user, edge);
        productionArtworkId = imported.importResult.Model.Id;
      }

      //const isEasyViewSpecialCase = isDTFTransfer === true && isEasyView === false;
      const editUrl = await getEasyViewEditConfigBaseURL$(
        productionArtworkId!,
        wizardDoc.id,
        user,
        token,
        false,
        artwork.userFileId,
        artwork.userFileConfig,
        edge,
        true
      );

      window.location.href = editUrl;

    } catch (error) {
      console.error('Failed to open EasyView editor:', error);
    } finally {
      setOpeningEV(false);
    }
  };

  const [dwsDesignData, setDwsDesignData] = useState<any>({}); // State to hold fetched design data

  useEffect(() => {
    if (appSnap.activeDesignDoc?.customerID) {
      fetchDefaultCustomerData();
    }
  }, [appSnap.activeDesignDoc?.customerID]);

  const fetchDefaultCustomerData = async () => {
    if (!appSnap.activeDesignDoc?.customerID) return;

    try {
      const headers = addAuthorizationHeader({
        'Content-Type': 'application/json'
      });

      const response = await fetch(`${getBackendApiUrl()}/customer-data/${appSnap.activeDesignDoc?.customerID}`, {
        method: 'GET',
        headers
      });

      if (!response.ok) {
        throw new Error("Error fetching customer data: " + await response.text());
      }

      const data = await response.json();

      setDefaultCustomerData({
        email: data.emails && data.emails.length > 0 ? data.emails[0] : '',
        fullName: data.fullName || '',
        phone: data.phone || ''
      });
    } catch (err) {
      console.error("Failed to fetch customer data:", err);
    }
  };

  useEffect(() => {
    const fetchDesignData = async () => {
      const response = await fetch(
        `${getBackendAPIDomain()}/stahls/13/artworkupload/mapjobdatadetails?mapName=cart`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(wizardDoc),
        }
      );
      const data = await response.json();
      setDwsDesignData(Object.keys(data).length > 0 ? data : null);
    };

    if (isWizardDesign)
      fetchDesignData();
  }, [isWizardDesign]);

  useEffect(() => {
    if (customerId && !isIncomplete) {
      setActiveImage('productionArtworkPreview');
      getLastSewFile(wizardDoc?.productionArtwork as ArtworkType);
    }
  }, [customerId, isIncomplete]);

  useEffect(() => {
    if (!isIncomplete && customerId && (isEasyView || (isWizardDesign && productType != 'CustomProduct' && productType != 'CustomPatchesEmbroidered' && productType != 'CustomPatchesVintage'))) {
      getPriceBreaks(wizardDocData).then(breaks => {
        if (!!breaks?.Multiple || breaks?.PriceBreaks) {
          setPriceBreaks(breaks.PriceBreaks)
          setPriceBreaksMultiple(breaks.Multiple)
        }
      })
    }
  }, [customerId, productType, isWizardDesign, isIncomplete]);

  useEffect(() => {
    if (currentPage === AppPages.DetailsProduction && activeImage != "productionArtwork") {
      setActiveImage("productionArtwork");
    }
  }, [currentPage]);

  const [internalNotes, setInternalNotes] = useState<WizardDocNote[]>([]);

  async function fetchInternalNotes(artId: string | null): Promise<WizardDocNote[]> {
    const res = await fetch(`${getBackendApiUrl()}/DesignNotes/${artId}`, {
      method: "GET",
      headers: addAuthorizationHeader({}),
    });

    if (!res.ok) {
      throw new Error(await res.text());
    }

    return res.json();
  }

  const refreshNotes = async () => {
    if (!adminId || !docId) return;
    try {
      const notes = await fetchInternalNotes(docId);
      setInternalNotes(notes);
    } catch (err) {
      alert('Failed to fetch design notes: ' + err);
    }
  };

  const refreshSewFilePrice = async() => {
    if(docId && wizardDocData.sewFile === "yes") {
      await getOriginalSewDiskChargesForDocumentId(docId)
      const sewDiskCharges = await getSewDiskChargesForDocumentId(docId)
      const [sewDiskCharge] = sewDiskCharges;
      setCurrentSewDiskCharge(sewDiskCharge ? sewDiskCharge : undefined);
    }
  }

  const refreshArtFeePrice = async() => {
    try {
      if (!!docId && docId == originalDocID
        && !!wizardDoc?.orderID
        && !!wizardDoc.lineID
        && (wizardDoc.docType == "WizardDesign"
          || (isAWAWProductType(productType) && wizardDocData?.ProofOnly == 1)
          || (isEasyView && !wizardDocData?.printAsIs))
        && wizardDoc.wizardID != "CustomProduct") {
        const artFee = await getBCArtFeeForDocumentId(docId)
        setArtFeeData(artFee)
      }
      else {
        setArtFeeData(undefined)
      }
    } catch {
      setArtFeeData(undefined)
    }
  }

  useEffect(() => {
    if(!isIncomplete) {
      refreshNotes();
      refreshSewFilePrice();
      refreshArtFeePrice();
    }
  }, [docId, adminId, isIncomplete]);

  if (wizardDoc?.productionArtworkPreview && wizardDoc?.productionArtworkPreview !== productionArtworkPreview) {
    setProductionArtworkPreview(wizardDoc.productionArtworkPreview as ArtworkType)
  }

  if (wizardDoc?.productionArtwork && wizardDoc?.productionArtwork !== productionArtwork) {
    setProductionArtwork(wizardDoc.productionArtwork as ArtworkType)
  }

  if (!originalArtwork && wizardDoc?.originalArtwork) {
    const updatedOriginalArtwork = wizardDoc.originalArtwork.map((item) => ({
      ...item,
      originalFileName: wizardDoc.wizardData.origFileName,
    }));
    setOriginalArtwork(updatedOriginalArtwork as ArtworkType);
  }

  const handleButtonClick = (imageType: ArtworkFolder) => {
    setActiveImage(imageType);
  };

  const getLastSewFile = (productionArtworkImages: ArtworkType) => {
    const sewFiles = productionArtworkImages && productionArtworkImages?.filter(file => file.fileRole === "SewFile");
    if (sewFiles && sewFiles.length > 0)
      setSewFileImg(sewFiles[sewFiles.length - 1])
  };

  const downloadSewFile = async () => {
    if (!sewFileImg) {
      console.error('sewFileImg is null');
      return;
    }

    try {
      const { fileRole, userFileId } = sewFileImg;
      const extension = getFileExtension(userFileId ?? '');
      const imageUrl = generateDownloadOriginalArtworkURL(sewFileImg, DEFAULT_IMAGE);

      await downloadProductionFile(imageUrl, fileRole, "productionArtwork", extension);
    } catch (error) {
      console.error('Error downloading sew file:', error);
    }
  };

  const onUploadFinished = (data: ArtworkItemType, artType: ArtworkFolder) => {
    switch (artType) {
      case 'originalArtwork':
        const originalArtworkData = originalArtwork ? [...originalArtwork, data] : [data];
        setOriginalArtwork(originalArtworkData);
        break;
      case 'productionArtwork':
        const productionArtworkData = productionArtwork ? [...productionArtwork, data] : [data];
        setProductionArtwork(productionArtworkData);
        break;
      case 'productionArtworkPreview':
        const productionArtworkPreviewData = productionArtworkPreview ? [...productionArtworkPreview, data] : [data];
        setProductionArtworkPreview(productionArtworkPreviewData);
        break;
      default:
        console.error('Unknown art type');
    }
    updateDesignDetails()
  };

  function switchArtType(folder: ArtworkFolder) {
    switch (folder) {
      case 'originalArtwork': return originalArtwork
      case 'productionArtwork': return productionArtwork
      case 'productionArtworkPreview': return productionArtworkPreview
    }
  }

  function ImageTabs() {
    return <div className={styles.buttonsContainer}>
      <button
        style={{
          cursor: "pointer",
          fontSize: customerId ? "100%" : "unset",
          height: customerId ? "unset" : "100%"
        }}
        type="button"
        onClick={() => handleButtonClick('originalArtwork')}
        className={activeImage === 'originalArtwork' ? styles.activeButton : ''}>
        Original Files
      </button>
      {!isIncomplete && <button
        style={{
          cursor: "pointer",
          fontSize: customerId ? "100%" : "unset",
          height: customerId ? "unset" : "100%"
        }}
        type="button"
        onClick={() => handleButtonClick('productionArtworkPreview')}
        className={activeImage === 'productionArtworkPreview' ? styles.activeButton : ''}>
        Proof Files
      </button>}

      <PageSwitch If={[AppPages.DetailsArtist, AppPages.DetailsProduction]}>
        <button
          type="button"
          style={{
            cursor: "pointer",
            fontSize: customerId ? "100%" : "unset",
            height: customerId ? "unset" : "100%"
          }}
          onClick={() => handleButtonClick('productionArtwork')}
          className={activeImage === 'productionArtwork' ? styles.activeButton : ''}>
          Production Artwork
        </button>
      </PageSwitch>
    </div>
  }

  if (!wizardDoc)
    return <ArtDetailsLoading />
  else
    return (
      <>
        <div className={styles.wrapper}>
          <MainBar
            adminId={adminId}
            page={currentPage}
            orderNo={orderNo}
            lineNo={lineNo}
            archived={archived}
            itemId={wizardDoc.id}
            isIncomplete={isIncomplete} />

          {/* Disclaimer */}
          {!isApproved &&
            <div className={styles.disclaimerText}>
              <b>Disclaimer:</b> Unapproved artwork with no activity for 90 days will be automatically deleted.
            </div>
          }

          {/* TOP Image and Actions */}
          <div className={styles.container}>

            {/* LEFT TOP */}
            <div className={[styles.column, styles.dualImg].join(' ')}>

              {/* Conditionally render ImageTabs */}
              {<ImageTabs />}

              <div className={[styles.imageContainer, styles.loaded].join(' ')}>

                {/* Image Manager */}
                <div className={styles.tabsContainer}>
                  <ImageSelector
                    imageType={activeImage}
                    documentId={wizardDoc.id}
                    onImageSelect={handleImageSelect}
                    imageData={switchArtType(activeImage)}
                    className={styles.pArtworkImg}
                    disabledButtonClass={disabledButtonClass}
                    alt={activeImage}
                    isAdmin={!!adminId}
                    isEasyView={isEasyView}
                    docType={wizardDoc?.docType}
                    width={wizardDocData.artWidth}
                    height={wizardDocData.artHeight}
                    orderNumber={orderNo}
                    customerNumber={appSnap.customerId || appSnap.adminId }
                    isDTF={wizardDoc.docType == 'EasyView' || wizardDoc.docType=='WizardDesign' && wizardDoc.wizardID=='UltraColorDTFTransfers' || wizardDoc.docType=='WizardDesign' && wizardDoc.wizardID=='DTFTransfer'}
                  />
                </div>
              </div>

              <div className={styles.bottomImgWrapper}>
                {/* Upload Image */}
                <PageSwitch If={[AppPages.DetailsArtist]}>
                  <FileUploadComponent
                    id={wizardDoc.id}
                    productType={productType}
                    onUploadFinished={(data: ArtworkItemType, artType: ArtworkFolder) => onUploadFinished(data, artType)}
                  />
                </PageSwitch>

                {/* Preview Image */}
                {imageSrc !== DEFAULT_IMAGE &&
                  <button
                    type="button"
                    className={`${artStyles.orangeBtn} flex flex-row gap-1 text-white`}
                    style={{ cursor: "pointer" }}
                    onClick={() => setOpenImagePreview(true)}>
                    Preview
                  </button>
                }
                {enableEasyViewLink && (
                  <button
                    type="button"
                    disabled={openingEV}
                    className={`${artStyles.orangeBtn} flex flex-row gap-1 text-white`}
                    style={{ cursor: openingEV ? "default" : "pointer", opacity: openingEV ? 0.6 : 1 }}
                    onClick={openInEasyViewClick}
                  >
                    {openingEV ? "Opening..." : "Open In EasyView"}
                  </button>
                )}
                <ImagePreview
                  isOpen={openImagePreview}
                  onClose={() => setOpenImagePreview(false)}
                  imageSrc={imageSrc} />
              </div>
            </div>

            {/* RIGHT TOP */}
            <div className={styles.column}>
              <PageSwitch If={[AppPages.DetailsProduction]}>
                <div className={styles.orderInfoContainer}>
                  <div className={styles.orderInfoRow}>
                    <span className={styles.oiTitle}>Work Order No.</span>
                    <span>{orderNo}</span>
                  </div>
                  <div className={styles.orderInfoRow}>
                    <span className={styles.oiTitle}>Shipment Date</span>
                    <span>{appSnap.prodLineItems[0]?.duedate}</span>
                  </div>
                  <div className={styles.orderInfoRow}>
                    <span className={styles.oiTitle}>Ship</span>
                    <span>{shippingAgentName}/{shippingAgentServiceDescription}</span>
                  </div>
                  {isLNTransfer ?
                    <>
                      <div className={styles.orderInfoRow}>
                        <span className={styles.oiTitle}>Quantity of kits</span>
                        <span>{appSnap.prodLineItems[0]?.quantity}</span>
                      </div>
                      {wizardDoc?.wizardData?.quantity !== undefined && (
                        <div className={styles.orderInfoRow}>
                          <span className={styles.oiTitle}>Number of items per kit</span>
                          <span>{wizardDoc.wizardData.quantity}</span>
                        </div>
                      )}
                    </>
                    :
                    <div className={styles.orderInfoRow}>
                      <span className={styles.oiTitle}>Quantity</span>
                      <span>{appSnap.prodLineItems[0]?.quantity}</span>
                    </div>
                  }
                  <div className={styles.orderInfoRow}>
                    <span className={styles.oiTitle}>Sales Order No.</span>
                    <span>{appSnap.prodLineItems[0]?.salesorderno_vel}</span>
                  </div>
                </div>
              </PageSwitch>

              <PageSwitch If={[AppPages.DetailsCustomer, AppPages.DetailsArtist]}>
                <div className={styles.communicationContainer}>
                  <UserWorkFlow
                    designId={wizardDoc.id}
                    designStatus={wizardDoc.statusID}
                    conversations={wizardDocConversations}
                    isAdminMode={!!adminId}
                    archived={!!wizardDoc.archived}
                    allowThreads={false}
                    designCustomerId={wizardDoc.customerID}
                    refreshNotes={refreshNotes}
                    defaultCustomerData={defaultCustomerData}
                    refreshSewFilePrice={refreshSewFilePrice}
                    refreshArtFeePrice={refreshArtFeePrice}
                  />
                </div>
              </PageSwitch>
            </div>
          </div>

          {/* ART CENTER ( Middle Area ) */}
          <div className={styles.artCenterContainer}>
            <div className={styles.artCenter}>
              <span className={styles.title}>ART CENTER</span>
              <div className={styles.artCenterWrapper}>
                <div className={styles.leftLabels}>
                  <span>Status:</span>
                  <span>Account #</span>
                  {(wizardDoc?.notificationsEmail || defaultCustomerData?.email) &&
                    <span>Contact Email:</span>
                  }
                  {wizardDoc?.contactName &&
                    <span>Contact Name:</span>
                  }
                  {(wizardDoc?.contactPhone || defaultCustomerData?.phone) &&
                    <span>Contact Phone:</span>
                  }
                  <span>Design ID</span>
                  {currentSewDiskCharge &&
                    <span className={styles.orangeHighlight}>Sew Fee:</span>
                  }
                  {artFeeData &&
                    <span className={styles.orangeHighlight}>Art Fee:</span>
                  }
                  {(!!lnTailPrice && !isAWAWProductType(productType)) &&
                    <span className={styles.orangeHighlight}>Tail Fee{wizardDoc.isLNProof ? " (per unit)" : ""}:</span>
                  }
                  {(!!lnTailCutInPrice && !isAWAWProductType(productType)) &&
                    <span className={styles.orangeHighlight}>Cut In Fee{wizardDoc.isLNProof ? " (per unit)" : ""}:</span>
                  }
                  {!!lnSewDiskPrice &&
                    <span className={styles.orangeHighlight}>Sew Disk Fee:</span>
                  }
                  {!!lnNameDropPrice &&
                    <span className={styles.orangeHighlight}>Name Drop Fee{(wizardDoc.isLNProof && wizardDocData?.ProofOnly != 1) ? " (per unit)" : ""}:</span>
                  }
                  {(!!lnVelcroPrice && !isAWAWProductType(productType)) &&
                    <span className={styles.orangeHighlight}>Velcro Fee{wizardDoc.isLNProof ? " (per unit)" : ""}:</span>
                  }
                </div>
                <div className={styles.rightValues}>
                  <div className={styles.info}>
                    <span className={styles.details}>{currentPage === AppPages.DetailsHistory ? "Approved" : activeStatusDescription}</span>
                    <span className={[styles.badge, (statusColor ? styles[statusColor] : "")].join(' ')}></span>
                    <div className={styles.info}>
                      {docObject?.updateDate &&
                        <span className={styles.details}>
                          Last Modified: {(formattedDate({ dateString: docObject?.updateDate, format: 'MM/DD/YYYY' }) ?? "--")}
                        </span>
                      }
                    </div>
                  </div>
                  <div className={styles.info}>
                    <span className={styles.details}>{wizardDoc.customerID}</span>
                  </div>
                  {(wizardDoc?.notificationsEmail || defaultCustomerData?.email) && (
                    <div className={styles.info}>
                      <span className={styles.details}>
                        {!wizardDoc?.notificationsEmail && defaultCustomerData?.email
                          ? `${defaultCustomerData?.email} (default)`
                          : wizardDoc?.notificationsEmail
                        }
                      </span>
                    </div>
                  )}
                  {wizardDoc?.contactName && (
                    <div className={styles.info}>
                      <span className={styles.details}>
                        {wizardDoc.contactName}
                      </span>
                    </div>
                  )}
                  {(wizardDoc?.contactPhone || defaultCustomerData?.phone) && (
                    <div className={styles.info}>
                      <span className={styles.details}>
                        {!wizardDoc?.contactPhone && defaultCustomerData?.phone
                          ? `${defaultCustomerData?.phone} (default)`
                          : wizardDoc?.contactPhone
                        }
                      </span>
                    </div>
                  )}
                  <div className={styles.info}>
                    <span className={styles.details}>{wizardDoc.id}</span>
                  </div>
                  {currentSewDiskCharge &&
                    <>
                      <div className={styles.info}>
                        <span className={[styles.details, styles.detailsOrangeHighlight].join(' ')}>
                          ${currentSewDiskCharge.price?.toFixed(2)}
                        </span>
                      </div>
                    </>
                  }
                  {artFeeData &&
                    <>
                      <div className={styles.info}>
                        <span className={[styles.details, styles.detailsOrangeHighlight].join(' ')}>
                          ${artFeeData.unitPrice?.toFixed(2)}
                        </span>
                      </div>
                    </>
                  }
                  {(!!lnTailPrice && !isAWAWProductType(productType)) &&
                    <>
                      <div className={styles.info}>
                        <span className={[styles.details, styles.detailsOrangeHighlight].join(' ')}>
                          ${lnTailPrice?.toFixed(2)}
                        </span>
                      </div>
                    </>
                  }
                  {(!!lnTailCutInPrice && !isAWAWProductType(productType)) &&
                    <>
                      <div className={styles.info}>
                        <span className={[styles.details, styles.detailsOrangeHighlight].join(' ')}>
                          ${lnTailCutInPrice?.toFixed(2)}
                        </span>
                      </div>
                    </>
                  }
                  {!!lnSewDiskPrice &&
                    <>
                      <div className={styles.info}>
                        <span className={[styles.details, styles.detailsOrangeHighlight].join(' ')}>
                          ${lnSewDiskPrice?.toFixed(2)}
                        </span>
                      </div>
                    </>
                  }
                  {!!lnNameDropPrice &&
                    <>
                      <div className={styles.info}>
                        <span className={[styles.details, styles.detailsOrangeHighlight].join(' ')}>
                          ${lnNameDropPrice?.toFixed(2)}
                        </span>
                      </div>
                    </>
                  }
                  {(!!lnVelcroPrice && !isAWAWProductType(productType)) &&
                    <>
                      <div className={styles.info}>
                        <span className={[styles.details, styles.detailsOrangeHighlight].join(' ')}>
                          ${lnVelcroPrice?.toFixed(2)}
                        </span>
                      </div>
                    </>
                  }
                </div>
              </div>
              <div className={styles.moreDetails}
                onClick={() => setOpenMoreDetailsModal(true)}>
                <ViewMoreIcon />
                <span> More Art Details</span>
              </div>
            </div>
            <div className={styles.artCenterBtns}>
              {(sewFileImg && !adminId) &&
                <button
                  type="button"
                  className={styles.orangeBtn}
                  style={{ cursor: "pointer" }}
                  onClick={() => downloadSewFile()}>
                  Download Sew File
                </button>
              }
            </div>
            {priceBreaks?.length > 0 &&
              <div className={styles.priceWrapper}>
                <span className={styles.title}>PRICE BREAKS*</span>
                <div className={styles.priceList}>
                  {priceBreaksMultiple && priceBreaksMultiple > 1 && <span className={styles.pPriceMultiple}>Quantity must be multiple of {new String(priceBreaksMultiple)}</span>}
                  {priceBreaks.map((item, index) => {
                    console.log(item)
                    return (<div key={`${item.FromQty}to${item.ToQty}`} className={styles.priceBreak}>
                      <p className={styles.pQty}>{index === priceBreaks.length - 1 ? item.FromQty + '+' : item.FromQty === item.ToQty ? `${item.ToQty}` : `${item.FromQty}-${item.ToQty}`}</p>
                      <p className={styles.pPrice}>{`$${item.ItemPrice?.toFixed(2)} ea`}</p>
                    </div>)
                  })}
                  <span className={styles.pPrice}><i>*Prices subject to change</i></span>
                </div>
              </div>
            }
          </div>

          {/* Details Columns */}
          <div>
            <Info
              isEasyView={isEasyView}
              isLNTransfer={isLNTransfer}
              isWizardDesign={isWizardDesign}
              isBarcelona={isBarcelona}
              isNhl={isNhl}
              isEmblemWizard={isEmblemWizard}
              originalDocId={originalDocID}
              productType={productType}
              fullWizardData={fullWizardData}
              extraDocData={docData}
              internalNotes={internalNotes}
              {...(dwsDesignData && isWizardDesign ? dwsDesignData : wizardDocData)}
            />
          </div>
        </div >

        {/* Printable Work Order aka Traveler  */}
        <div className={styles.printable}>
          <PrintTemplate
            wizardDocData={wizardDocData}
            woQR={{ orderNo, lineNo }}
            productionData={prodLineItems as IProdOrderLineItem[]}
            productionArtwork={productionArtwork}
            productionArtworkPreview={productionArtworkPreview}
            originalArtwork={originalArtwork}
            wizardDocFullData={wizardDocFullData}
          />
        </div>

        {/* More Art Details - Modal  */}
        <ArtDetailsModal
          isOpen={openMoreDetailsModal}
          onClose={() => setOpenMoreDetailsModal(false)}
          document={wizardDoc as DesignDoc}
          defaultCustomerData={defaultCustomerData}
        />
      </>
    );
}

export default DesignDetailPage 